import { truncateToTwoDecimals } from "@/_old/utils/number";
import { CircularProgress } from "@/core/components/ui/circular-progress";
import { DashboardCard } from "@/core/components/ui/dashboard-card";
import { useCancellationRate } from "../hooks/use-cancellation-rate";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DashboardEmptyState } from "../../dashboard-empty-state";

interface ReservationCancellationsCardProps {
  startDate: string;
  endDate: string;
  companyId: number;
  cities?: string[] | null;
  spaces?: number[] | null;
  users?: number[] | null;
  status?: number[] | null;
  types?: string[] | null;
  group_id?: number | null;
}

export function ReservationCancellationsCard({
  startDate,
  endDate,
  companyId,
  cities,
  spaces,
  users,
  status,
  types,
  group_id,
}: ReservationCancellationsCardProps) {
  const [t] = useTranslation(["dashboard"]);

  const { data, isLoading } = useCancellationRate({
    startDate,
    endDate,
    companyId,
    cities,
    spaces,
    users,
    status,
    types,
    group_id,
  });

  const verifyEmptyData = useMemo(() => {
    return !isLoading && (!data || data?.cancellationRate === 0);
  }, [data, isLoading]);

  const cancellationStatus = useMemo((): {
    type: "danger" | "default" | "good" | "warning";
    label: string;
    color: string;
  } => {
    if (!data?.cancellationRate || data?.cancellationRate === 0) {
      return {
        type: "good",
        label: t("dashboard-cancellation-healthy-index", "Índice saudável"),
        color: "text-teal-600",
      };
    }

    if (data.cancellationRate <= 5) {
      return {
        type: "good",
        label: t("dashboard-cancellation-healthy-index", "Índice saudável"),
        color: "text-teal-600",
      };
    }

    if (data.cancellationRate <= 10) {
      return {
        type: "warning",
        label: t("dashboard-cancellation-moderate-index", "Índice moderado"),
        color: "text-amber-600",
      };
    }

    return {
      type: "danger",
      label: t("dashboard-cancellation-high-index", "Índice alto"),
      color: "text-red-600",
    };
  }, [data, t]);

  if (isLoading) {
    return (
      <DashboardCard
        className="bg-card border border-border shadow-sm"
        title={t(
          "dashboard-reservation-cancellations",
          "Cancelamentos de reservas",
        )}
      >
        <div className="flex flex-col gap-6 justify-center items-center">
          <div className="w-36 h-36 bg-gray-200 rounded-full animate-pulse" />
          <div className="mx-auto w-16 h-4 bg-gray-200 rounded animate-pulse" />
        </div>
      </DashboardCard>
    );
  }

  if (verifyEmptyData) {
    return (
      <DashboardCard
        className="bg-card border border-border shadow-sm"
        title={t(
          "dashboard-reservation-cancellations",
          "Cancelamentos de reservas",
        )}
      >
        <div className="flex items-center justify-center h-[200px]">
          <DashboardEmptyState />
        </div>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard
      className="bg-card border border-border shadow-sm"
      title={t(
        "dashboard-reservation-cancellations",
        "Cancelamentos de reservas",
      )}
    >
      <div className="flex flex-col gap-6 justify-center items-center">
        <CircularProgress
          value={
            data?.cancellationRate
              ? truncateToTwoDecimals(data.cancellationRate)
              : 0
          }
          color={cancellationStatus.color}
          label="Reservas"
        />
        <p className={`text-sm ${cancellationStatus.color}`}>
          {cancellationStatus.label}
        </p>
      </div>
    </DashboardCard>
  );
}
